import { shallowRef, ref, computed } from '@vue/composition-api';

import type { Connection } from '@/types';
import { container, Tokens } from '@/di';
import type { SalesforceService } from '@/services';

const salesforceService = container.get<SalesforceService>(
  Tokens.SalesforceService
);

export const sourceConnection = shallowRef<Connection | null>(null);
export const targetConnection = shallowRef<Connection | null>(null);
const loading = ref(false);

const connections = computed(() => {
  return {
    source: sourceConnection.value,
    target: targetConnection.value,
  };
});

export const isSourceConnected = computed(
  () => sourceConnection.value !== null
);
export const isTargetConnected = computed(
  () => targetConnection.value !== null
);

export const connected = computed(
  () => isSourceConnected.value && isTargetConnected.value
);

async function refresh(): Promise<void> {
  if (loading.value) {
    return;
  }

  try {
    loading.value = true;
    const result = await salesforceService.refreshConnections();

    sourceConnection.value = result.source;
    targetConnection.value = result.target;
  } finally {
    loading.value = false;
  }
}

export async function getConnections(): Promise<{
  source: Connection | null;
  target: Connection | null;
}> {
  if (sourceConnection.value !== null && targetConnection.value !== null) {
    return {
      source: sourceConnection.value,
      target: targetConnection.value,
    };
  }

  try {
    loading.value = true;
    const result = await salesforceService.getConnections();

    sourceConnection.value = result.source;
    targetConnection.value = result.target;

    return result;
  } finally {
    loading.value = false;
  }
}

export const isAdvancedApprovalsSupported = computed(() => {
  if (sourceConnection.value === null) {
    return false;
  }
  if (targetConnection.value === null) {
    return false;
  }
  return (
    sourceConnection.value.config.enableAdvancedApprovals &&
    targetConnection.value.config.enableAdvancedApprovals
  );
});

export const isBillingSupported = computed(() => {
  if (sourceConnection.value === null) {
    return false;
  }
  if (targetConnection.value === null) {
    return false;
  }
  return (
    sourceConnection.value.config.enableBilling &&
    targetConnection.value.config.enableBilling
  );
});

export default function useConnections() {
  return {
    sourceConnection,
    targetConnection,
    connections,
    isSourceConnected,
    isTargetConnected,
    isAdvancedApprovalsSupported,
    isBillingSupported,
    connected,
    loading,
    refresh,
  };
}
